import { useState, useEffect } from 'react';
import api from './api/axiosInstance';
import Sidebar from './Sidebar.js';
import Breadcrumb from './Breadcrumb';
import { Container, Row, Col, Card, Image, Form, Button, Badge, InputGroup } from 'react-bootstrap';
import icUserCircle from './assets/img/ic-user-circle-bg.svg';
import icNewsLarge from './assets/img/ic-news-large.svg';
import icUniversityLarge from './assets/img/ic-university-large.svg';
import icUserGroupLarge from './assets/img/ic-user-group-large.svg';
import icChartUp from './assets/img/ic-chart-up.svg';
import icChartDown from './assets/img/ic-chart-down.svg';
import { exportReportingGeneralPdf } from './api/exportReportingGeneralPdf.js';

const userClassRows = [
    { label: 'Talent', key: 'talent' },
    { label: 'Company', key: 'company' },
    { label: 'Partner', key: 'partner' },
    { label: 'Abroadify', key: 'abroadify' },
];

const userTypeRows = [
    { label: 'User', key: 'user' },
    { label: 'Admin', key: 'admin' },
    { label: 'Super Admin', key: 'superAdmin' },
];

const userMetricTiles = [
    {
        labelPrefix: 'New users this',
        labelBold: 'month',
        value: '492',
        trendDirection: 'up',
        trendPercent: '92%',
        trendText: 'Up from last year',
    },
    {
        labelPrefix: 'New users this',
        labelBold: 'year',
        value: '2,732',
        trendDirection: 'up',
        trendPercent: '92%',
        trendText: 'Up from last year',
    },
    {
        labelPrefix: 'Churned this',
        labelBold: 'month',
        value: '239',
        trendDirection: 'up',
        trendPercent: '92%',
        trendText: 'Up from last year',
    },
    {
        labelPrefix: 'Churned this',
        labelBold: 'year',
        value: '928',
        trendDirection: 'down',
        trendPercent: '13%',
        trendText: 'Down from last month',
    },
];

const activityViewRows = [
    { label: 'Company profile views (non-admins)', key: 'companyProfileViews' },
    { label: 'Open job views', key: 'openJobViews' },
    { label: 'City insights views', key: 'cityInsightsViews' },
    { label: 'Salary insights views', key: 'salaryInsightsViews' },
];

const activityActionRows = [
    { label: 'Comments posted', key: 'commentsPosted' },
    { label: 'Invited users', key: 'invitedUsers' },
    { label: 'Chats initiated / Messages exchanged', key: 'chatsMessages' },
];

const companyGeoRows = [
    { label: 'Continents', key: 'continents' },
    { label: 'Countries', key: 'countries' },
    { label: 'Cities', key: 'cities' },
];

const companyActionRows = [
    { label: 'Jobs posted', key: 'jobsPosted' },
    { label: 'Job applications sent', key: 'jobApplicationsSent' },
];

const totalPartners = 0;

const partnerGeoRows = [
    { label: 'Continents', value: 4 },
    { label: 'Countries', value: 32 },
    { label: 'Cities', value: 123 },
];

const partnerActionRows = [
    { label: 'Students uploaded', value: 1203 },
];

const formatCount = (value) => (typeof value === 'number' ? value.toLocaleString() : '—');

const formatDisplayDate = (isoDate) => {
    if (!isoDate) return '';
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

const ReportingGeneralList = () => {
    const [tickets, setTickets] = useState([]);
    const [generalStats, setGeneralStats] = useState(null);
    const [activityStats, setActivityStats] = useState(null);
    const [companyStats, setCompanyStats] = useState(null);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const hasPeriodFilter = Boolean(fromDate || toDate);

    const clearPeriodFilter = () => {
        setFromDate('');
        setToDate('');
    };

    const handleExportPdf = async () => {
        try {
            await exportReportingGeneralPdf({ generalStats, activityStats, companyStats, fromDate, toDate });
        } catch (error) {
            console.error('Error exporting reporting PDF:', error);
            alert('An error occurred while exporting the reporting PDF.');
        }
    };

    useEffect(() => {
        const fetchTickets = async () => {
            try {
                const res = await api.get('/api/tickets');
                setTickets(res.data);
            } catch (error) {
                console.error('Error fetching tickets:', error);
            }
        };
        fetchTickets();
    }, []);

    useEffect(() => {
        const fetchGeneralStats = async () => {
            try {
                const params = {};
                if (fromDate) params.fromDate = fromDate;
                if (toDate) params.toDate = toDate;

                const res = await api.get('/api/reporting/general', { params });
                setGeneralStats(res.data);
            } catch (error) {
                console.error('Error fetching general report:', error);
            }
        };
        fetchGeneralStats();
    }, [fromDate, toDate]);

    useEffect(() => {
        const fetchCompanyStats = async () => {
            try {
                const params = {};
                if (fromDate) params.fromDate = fromDate;
                if (toDate) params.toDate = toDate;

                const res = await api.get('/api/reporting/companies', { params });
                setCompanyStats(res.data);
            } catch (error) {
                console.error('Error fetching company report:', error);
            }
        };
        fetchCompanyStats();
    }, [fromDate, toDate]);

    useEffect(() => {
        const fetchActivityStats = async () => {
            try {
                const params = {};
                if (fromDate) params.fromDate = fromDate;
                if (toDate) params.toDate = toDate;

                const res = await api.get('/api/reporting/activities', { params });
                setActivityStats(res.data);
            } catch (error) {
                console.error('Error fetching activity report:', error);
                setActivityStats(null);
            }
        };
        fetchActivityStats();
    }, [fromDate, toDate]);

    return (
        <>
            <Sidebar ticketCount={tickets.length} />
            <main className="main-content flex-grow-1">
                <Container>
                    <Row>
                        <Col>
                            <Breadcrumb />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Card className="mb-4">
                                <Card.Body>
                                    <Row>
                                        <Col xs={12}>
                                            <h1 className="mb-4">General</h1>
                                        </Col>
                                    </Row>
                                    <div className="filter">
                                        <Row className="g-3 align-items-end flex-nowrap">
                                            <Col md={6} lg={4}>
                                                <Form.Group>
                                                    <Form.Label htmlFor="reportingPeriodFrom">Period</Form.Label>
                                                    <InputGroup>
                                                        <Form.Control
                                                            id="reportingPeriodFrom"
                                                            type="date"
                                                            value={fromDate}
                                                            max={toDate || undefined}
                                                            onChange={(e) => setFromDate(e.target.value)}
                                                            className="custom-date-input"
                                                        />
                                                        <Form.Control
                                                            id="reportingPeriodTo"
                                                            type="date"
                                                            value={toDate}
                                                            min={fromDate || undefined}
                                                            onChange={(e) => setToDate(e.target.value)}
                                                            className="custom-date-input"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col xs="auto" className="ms-auto">
                                                <Button variant="primary" className="btn-sm" onClick={handleExportPdf}>
                                                    Export results
                                                </Button>
                                            </Col>
                                        </Row>

                                        {hasPeriodFilter && (
                                            <Row className="filter-results">
                                                <Col className="d-flex align-items-center flex-wrap gap-2">
                                                    <span>Showing results:</span>
                                                    {fromDate && (
                                                        <Badge bg="info">
                                                            From {formatDisplayDate(fromDate)}{' '}
                                                            <Button variant="link" size="sm" onClick={() => setFromDate('')}>
                                                                ×
                                                            </Button>
                                                        </Badge>
                                                    )}
                                                    {toDate && (
                                                        <Badge bg="info">
                                                            To {formatDisplayDate(toDate)}{' '}
                                                            <Button variant="link" size="sm" onClick={() => setToDate('')}>
                                                                ×
                                                            </Button>
                                                        </Badge>
                                                    )}
                                                    <Button variant="link" size="sm" className="ms-auto" onClick={clearPeriodFilter}>
                                                        Clear all
                                                    </Button>
                                                </Col>
                                            </Row>
                                        )}
                                    </div>
                                    <div className="section-box">
                                        <Row className="mb-4">
                                            <Col className="d-flex justify-content-between align-items-center">
                                                <h3 className="mb-0"><span className="item-number">1.</span> Users</h3>
                                                <span className="link">See User Charts</span>
                                            </Col>
                                        </Row>
                                        <hr />
                                        <Row className="mb-4">
                                            <Col md={4}>
                                                <div className="stat-box">
                                                    <div className="stat-box-icon">
                                                        <Image src={icUserCircle} alt="Total Users" />
                                                    </div>
                                                    <span className="stat-box-label mb-3">Total Users</span>
                                                    <span className="stat-box-value">{formatCount(generalStats?.totalUsers)}</span>
                                                </div>
                                            </Col>
                                            <Col md={4}>
                                                <div className="stat-list">
                                                    <div className="stat-list-title">User Class</div>
                                                    {userClassRows.map((row) => (
                                                        <div className="stat-list-row" key={row.label}>
                                                            <span>{row.label}</span>
                                                            <strong>{formatCount(generalStats?.userClass?.[row.key])}</strong>
                                                        </div>
                                                    ))}
                                                </div>
                                            </Col>
                                            <Col md={4}>
                                                <div className="stat-list">
                                                    <div className="stat-list-title">User Types</div>
                                                    {userTypeRows.map((row) => (
                                                        <div className="stat-list-row" key={row.label}>
                                                            <span>{row.label}</span>
                                                            <strong>{formatCount(generalStats?.userTypes?.[row.key])}</strong>
                                                        </div>
                                                    ))}
                                                </div>
                                            </Col>
                                        </Row>
                                        <hr />
                                        <Row>
                                            {userMetricTiles.map((tile) => (
                                                <Col md={3} key={`${tile.labelPrefix}-${tile.labelBold}`}>
                                                    <div className="mini-stat-box">
                                                        <div className="mini-stat-box-label mb-3">
                                                            {tile.labelPrefix} <strong>{tile.labelBold}</strong>
                                                        </div>
                                                        <div className="mini-stat-box-value">{tile.value}</div>
                                                        <div className={`mini-stat-box-trend ${tile.trendDirection}`}>
                                                            <Image src={tile.trendDirection === 'up' ? icChartUp : icChartDown} alt="" />
                                                            <span className="mini-stat-box-trend-percent">{tile.trendPercent}</span>
                                                            <span>{tile.trendText}</span>
                                                        </div>
                                                    </div>
                                                </Col>
                                            ))}
                                        </Row>
                                    </div>
                                    <div className="section-box mt-4">
                                        <Row className="mb-4">
                                            <Col className="d-flex justify-content-between align-items-center">
                                                <h3 className="mb-0"><span className="item-number">2.</span> Activities</h3>
                                            </Col>
                                        </Row>
                                        <hr />
                                        <Row className="mb-4">
                                            <Col md={4}>
                                                <div className="stat-box">
                                                    <div className="stat-box-icon">
                                                        <Image src={icNewsLarge} alt="Total Activities" />
                                                    </div>
                                                    <span className="stat-box-label mb-3">Total Activities</span>
                                                    <span className="stat-box-value">{formatCount(activityStats?.totalActivities)}</span>
                                                </div>
                                            </Col>
                                            <Col md={4}>
                                                <div className="stat-list">
                                                    <div className="stat-list-title">Views</div>
                                                    {activityViewRows.map((row) => (
                                                        <div className="stat-list-row" key={row.label}>
                                                            <span>{row.label}</span>
                                                            <strong>{formatCount(activityStats?.views?.[row.key])}</strong>
                                                        </div>
                                                    ))}
                                                </div>
                                            </Col>
                                            <Col md={4}>
                                                <div className="stat-list">
                                                    <div className="stat-list-title">Actions</div>
                                                    {activityActionRows.map((row) => (
                                                        <div className="stat-list-row" key={row.label}>
                                                            <span>{row.label}</span>
                                                            <strong>{formatCount(activityStats?.actions?.[row.key])}</strong>
                                                        </div>
                                                    ))}
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>
                                    <div className="section-box mt-4">
                                        <Row className="mb-4">
                                            <Col className="d-flex justify-content-between align-items-center">
                                                <h3 className="mb-0"><span className="item-number">3.</span> Company data</h3>
                                            </Col>
                                        </Row>
                                        <hr />
                                        <Row>
                                            <Col md={4}>
                                                <div className="stat-box">
                                                    <div className="stat-box-icon">
                                                        <Image src={icUniversityLarge} alt="Total Companies" />
                                                    </div>
                                                    <span className="stat-box-label mb-3">Total Companies</span>
                                                    <span className="stat-box-value">{formatCount(companyStats?.totalCompanies)}</span>
                                                </div>
                                            </Col>
                                            <Col md={4}>
                                                <div className="stat-list">
                                                    <div className="stat-list-title">Geo statistics</div>
                                                    {companyGeoRows.map((row) => (
                                                        <div className="stat-list-row" key={row.label}>
                                                            <span>{row.label}</span>
                                                            <strong>{formatCount(companyStats?.geo?.[row.key])}</strong>
                                                        </div>
                                                    ))}
                                                </div>
                                            </Col>
                                            <Col md={4}>
                                                <div className="stat-list">
                                                    <div className="stat-list-title">Actions</div>
                                                    {companyActionRows.map((row) => (
                                                        <div className="stat-list-row" key={row.label}>
                                                            <span>{row.label}</span>
                                                            <strong>{formatCount(companyStats?.actions?.[row.key])}</strong>
                                                        </div>
                                                    ))}
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>
                                    {totalPartners > 0 && (
                                    <div className="section-box mt-4">
                                        <Row className="mb-4">
                                            <Col className="d-flex justify-content-between align-items-center">
                                                <h3 className="mb-0"><span className="item-number">4.</span> Partners data</h3>
                                                <span className="link">See Partner Charts</span>
                                            </Col>
                                        </Row>
                                        <hr />
                                        <Row>
                                            <Col md={4}>
                                                <div className="stat-box">
                                                    <div className="stat-box-icon">
                                                        <Image src={icUserGroupLarge} alt="Total Partners" />
                                                    </div>
                                                    <span className="stat-box-label mb-3">Total Partners</span>
                                                    <span className="stat-box-value">{formatCount(totalPartners)}</span>
                                                </div>
                                            </Col>
                                            <Col md={4}>
                                                <div className="stat-list">
                                                    <div className="stat-list-title">Geo statistics</div>
                                                    {partnerGeoRows.map((row) => (
                                                        <div className="stat-list-row" key={row.label}>
                                                            <span>{row.label}</span>
                                                            <strong>{formatCount(row.value)}</strong>
                                                        </div>
                                                    ))}
                                                </div>
                                            </Col>
                                            <Col md={4}>
                                                <div className="stat-list">
                                                    <div className="stat-list-title">Actions</div>
                                                    {partnerActionRows.map((row) => (
                                                        <div className="stat-list-row" key={row.label}>
                                                            <span>{row.label}</span>
                                                            <strong>{formatCount(row.value)}</strong>
                                                        </div>
                                                    ))}
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </main>
        </>
    );
};

export default ReportingGeneralList;
